import { useCallback, useState } from 'react'
import type { PerfilVeiculo } from '../../dominio/didatico/perfilVeiculo'
import { girarEmTorno, paraArray, vetor } from '../../dominio/geometria/vetor3'

/**
 * Comandos de câmera do ambiente 3D.
 *
 * Antes isto era um `useState` privado dentro de `Visualizador3D`, alcançável só
 * pelos três botões que moravam ao lado do canvas. A trilha de ferramentas
 * precisa dos mesmos comandos, e a barra de estado precisa saber qual vista está
 * ativa — por isso o contrato saiu de lá.
 *
 * Nada aqui decide conteúdo didático: são poses de observação. O que se vê não
 * muda, só de onde se olha.
 */

export type TipoComandoCamera =
  | 'aproximar'
  | 'afastar'
  | 'restaurar'
  | 'enquadrarSelecao'
  /** Todos os objetos selecionáveis no quadro (Table top: a cena montada inteira). */
  | 'enquadrarCena'
  | 'vistaFrontal'
  | 'vistaLateral'
  | 'vistaSuperior'
  | 'vistaIsometrica'
  /** Vista pela porta disponível, olhando para o habitáculo (Extração). */
  | 'interiorExtracao'
  /** Três quartos da peça em trabalho na oficina de corte. */
  | 'enquadrarOficina'
  /** Enquadra a vítima em foco junto com a prancha, quando houver uma ativa (Extração). */
  | 'alinhamentoVitimaPrancha'
  /** Retoma o enquadramento anterior ao último comando de vista/enquadramento. */
  | 'voltarEnquadramento'

export interface ComandoCamera {
  /** Cresce a cada acionamento: é o que faz repetir o mesmo comando ter efeito. */
  id: number
  tipo: TipoComandoCamera
  /** Objeto a enquadrar, quando o comando é `enquadrarSelecao` ou `alinhamentoVitimaPrancha`. */
  idAlvo?: string | null
}

export type Vetor = [number, number, number]

/** Prefixo dos objetos que podem ser enquadrados. Ver `componentes/Peca`. */
export const PREFIXO_SELECIONAVEL = 'selecionavel:'

/**
 * Como o veículo está apoiado, para as poses abaixo girarem junto com ele.
 *
 * Sem isso, toda pose calculada para um veículo em pé (`rolagemRad: 0`) olharia
 * para um ângulo sem sentido quando a Extração está com o veículo tombado ou
 * capotado — mesmo raciocínio de `postoDoVeiculo`, aqui aplicado à câmera em
 * vez do veículo. O padrão (`rolagemRad: 0, alturaApoio: 0`) preserva o
 * comportamento de sempre para os módulos que não giram o veículo.
 */
export interface PostoCamera {
  rolagemRad: number
  alturaApoio: number
}

export const SEM_ROLAGEM: PostoCamera = { rolagemRad: 0, alturaApoio: 0 }

const aplicarPosto = (v: Vetor, posto: PostoCamera): Vetor => {
  if (posto.rolagemRad === 0 && posto.alturaApoio === 0) return v
  const girado = girarEmTorno(vetor(...v), vetor(0, 0, 1), posto.rolagemRad)
  return paraArray(vetor(girado.x, girado.y + posto.alturaApoio, girado.z))
}

/** Enquadra o veículo inteiro: alvo na altura da cintura, sobre o eixo. */
export const alvoCamera = (perfil: PerfilVeiculo, posto: PostoCamera = SEM_ROLAGEM): Vetor =>
  aplicarPosto([0, perfil.cinturaY * 0.8, 0], posto)

/** Pose padrão: três quartos dianteiro pelo lado do motorista. */
export const POSICAO_PADRAO: Vetor = [4.9, 3.4, 5.3]

export const DISTANCIA_MINIMA = 2.4
export const DISTANCIA_MAXIMA = 16

/**
 * Raio de observação das vistas nomeadas, derivado do próprio veículo — um hatch
 * e um caminhão não se enquadram da mesma distância. O fator vem de enquadrar o
 * maior lado com folga nas bordas.
 */
export function raioDeObservacao(perfil: PerfilVeiculo): number {
  const maiorLado = Math.max(perfil.comprimento, perfil.largura, perfil.tetoY)
  return Math.min(DISTANCIA_MAXIMA, Math.max(DISTANCIA_MINIMA, maiorLado * 1.75))
}

/**
 * Posição de câmera de cada vista nomeada.
 *
 * As vistas não são ortográficas: a projeção continua em perspectiva, e elas
 * apenas alinham a câmera a um eixo. Uma vista superior perfeitamente a pino
 * deixaria os controles de órbita sem referência de rotação, então ela guarda um
 * desvio pequeno em Z.
 */
export function posicaoDaVista(
  tipo: TipoComandoCamera,
  perfil: PerfilVeiculo,
  posto: PostoCamera = SEM_ROLAGEM,
): Vetor | null {
  const raio = raioDeObservacao(perfil)
  const altura = perfil.cinturaY * 0.8

  const base = ((): Vetor | null => {
    switch (tipo) {
      case 'vistaFrontal':
        return [0, altura, raio]
      case 'vistaLateral':
        return [raio, altura, 0]
      case 'vistaSuperior':
        return [0, raio, raio * 0.08]
      case 'vistaIsometrica':
      case 'restaurar':
        return POSICAO_PADRAO
      default:
        return null
    }
  })()

  return base ? aplicarPosto(base, posto) : null
}

/** Comandos que deixam a câmera parada numa pose reconhecível. */
export const VISTAS_NOMEADAS = [
  'vistaFrontal',
  'vistaLateral',
  'vistaSuperior',
  'vistaIsometrica',
] as const satisfies readonly TipoComandoCamera[]

export type VistaNomeada = (typeof VISTAS_NOMEADAS)[number]

export function ehVistaNomeada(tipo: TipoComandoCamera): tipo is VistaNomeada {
  return (VISTAS_NOMEADAS as readonly TipoComandoCamera[]).includes(tipo)
}

export interface ControleCamera {
  comando: ComandoCamera
  executar: (tipo: TipoComandoCamera, idAlvo?: string | null) => void
  /** Última vista nomeada acionada, para marcar o botão correspondente. */
  vistaAtiva: VistaNomeada | null
}

export function useControleCamera(): ControleCamera {
  const [comando, setComando] = useState<ComandoCamera>({ id: 0, tipo: 'restaurar' })
  const [vistaAtiva, setVistaAtiva] = useState<VistaNomeada | null>(null)

  const executar = useCallback((tipo: TipoComandoCamera, idAlvo?: string | null) => {
    setComando((atual) => ({ id: atual.id + 1, tipo, idAlvo }))
    // Zoom e enquadramento não são vistas: partem de onde a câmera estiver e
    // param em lugar qualquer. Manter o botão de vista aceso depois deles
    // mentiria sobre onde a câmera ficou.
    setVistaAtiva(ehVistaNomeada(tipo) ? tipo : null)
  }, [])

  return { comando, executar, vistaAtiva }
}
